import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import {
  curriculumSelectionAtom,
  knowledgeMapsAtom,
  CurriculumSelection,
} from './Curriculumselectionatom.ts';
import { Grid, Paper } from '@mui/material';
import { userInformationAtom } from '../../../Services/_states/auth.js';

interface Props {
  onSelectionComplete?: (selection: CurriculumSelection) => void;
  onChange?: (selection: CurriculumSelection) => void;
  externalLoading?: boolean;
  colProps?: object;
}

export default function BoardClassSubjectSelector({
  onSelectionComplete,
  onChange,
  externalLoading = false,
  colProps,
}: Props) {
  const aiActions = AiActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const mobile = loggedInUser?.mobile || '';
  const [data, setData] = useState<any>(null);
  const [selection, setSelection] = useRecoilState(curriculumSelectionAtom);
  const [localKnowledgeMaps, setLocalKnowledgeMaps] =
    useRecoilState(knowledgeMapsAtom);

  const [loading, setLoading] = useState(false);

  const { organisation, board, classVal, subject } = selection;

  const fetchKnowledgeMaps = async (organisationId: string) => {
    if (!organisationId) return;

    try {
      setLoading(true);

      const data = await aiActions.getKnowledgeList(organisationId);

      setLocalKnowledgeMaps(data || []);
    } catch (error) {
      console.error('Error fetching knowledge maps:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onChange?.(selection);

    if (
      organisation?.organisation_id &&
      board &&
      classVal &&
      subject
    ) {
      onSelectionComplete?.(selection);
    }
  }, [
    selection,
    organisation,
    board,
    classVal,
    subject,
    onChange,
    onSelectionComplete,
  ]);

  const boardOptions = [
    ...new Set(localKnowledgeMaps.map((item) => item.board)),
  ]
    .filter(Boolean)
    .sort()
    .map((item) => ({
      label: item,
      value: item,
    }));

  const classOptions = [
    ...new Set(
      localKnowledgeMaps
        .filter((item) => item.board === board)
        .map((item) => item.class)
    ),
  ]
    .sort()
    .map((item) => ({
      label: `Class ${item}`,
      value: item,
    }));

  const subjectOptions = [
    ...new Set(
      localKnowledgeMaps
        .filter(
          (item) =>
            item.board === board &&
            item.class === classVal
        )
        .map((item) => item.subject)
    ),
  ]
    .sort()
    .map((item) => ({
      label: item,
      value: item,
    }));

  const isLoading = loading || externalLoading;



  const getEntityRecord = async (mobileNumber?: string) => {
    if (!mobileNumber) return;

    const formattedMobile = mobileNumber;
    try {
      const response = await aiActions.getRecordEntity(formattedMobile, {
        template_form_type: 'contact_form',
      });
      setData(response);
    } catch (error) {
      console.error('❌ Failed to get entity record', error);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Grid container spacing={1}>
        {/* Organisation */}
        <Grid item xs={12} sm={3} sx={{ mt: '-15px' }} {...colProps}>
          <OrganisationDropdown
            value={organisation}
            onChange={(org: any) => {
              setSelection({
                organisation: org || null,
                board: '',
                classVal: '',
                subject: '',
              });

              setLocalKnowledgeMaps([]);

              if (org?.organisation_id) {
                fetchKnowledgeMaps(org.organisation_id);
                getEntityRecord(mobile);
              }
            }}
          />
        </Grid>

        {/* Board */}
        <Grid item xs={12} sm={3} {...colProps}>
          <SearchableDropdown
            label="Board"
            options={boardOptions}
            labelKey="label"
            valueKey="value"
            value={
              board
                ? {
                  label: board,
                  value: board,
                }
                : null
            }
            disabled={!organisation}
            loading={isLoading}
            onChange={(val: any) =>
              setSelection((prev) => ({
                ...prev,
                board: val?.value || '',
                classVal: '',
                subject: '',
              }))
            }
          />
        </Grid>

        {/* Class */}
        <Grid item xs={12} sm={3} {...colProps}>
          <SearchableDropdown
            label="Class"
            options={classOptions}
            labelKey="label"
            valueKey="value"
            value={
              classVal
                ? {
                  label: `Class ${classVal}`,
                  value: classVal,
                }
                : null
            }
            disabled={!organisation || !board}
            loading={isLoading}
            onChange={(val: any) =>
              setSelection((prev) => ({
                ...prev,
                classVal: val?.value || '',
                subject: '',
              }))
            }
          />
        </Grid>

        {/* Subject */}
        <Grid item xs={12} sm={3} {...colProps}>
          <SearchableDropdown
            label="Subject"
            options={subjectOptions}
            labelKey="label"
            valueKey="value"
            value={
              subject
                ? {
                  label: subject,
                  value: subject,
                }
                : null
            }
            disabled={!organisation || !classVal}
            loading={isLoading}
            onChange={(val: any) =>
              setSelection((prev) => ({
                ...prev,
                subject: val?.value || '',
              }))
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}